import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAdapter, useRecent, useSnapshot, useTopology } from "@/store/site";
import { useUiPref } from "@/store/uiPrefs";
import { PageShell } from "./PageShell";
import { Card, CardHeader, CardTitle, GranBadge } from "@/components/ui/Card";
import { SocArc } from "@/components/ui/SocArc";
import { TimeRangeBar } from "@/components/TimeRangeBar";
import { HistoryChart, type HistorySeries } from "@/components/charts/HistoryChart";
import { BessSocPowerChart, bessChartSummary } from "@/components/charts/BessSocPowerChart";
import { SetpointTracker } from "@/components/charts/SetpointTracker";
import { AvailabilityRibbon } from "@/components/charts/AvailabilityRibbon";
import { EmptyState } from "@/components/ui/EmptyState";
import { ChartSkeleton } from "@/components/ui/ChartSkeleton";
import { useUrlTimeRange } from "@/hooks/useUrlTimeRange";
import { useUrlParamState } from "@/hooks/useUrlParamState";
import { useThemeVersion } from "@/hooks/useThemeVersion";
import { pushToast } from "@/hooks/useToasts";
import { bessTelemetryCatalog, TELEMETRY_GROUPS } from "@/data/telemetryCatalog";
import { useGlossary, type GlossaryKey } from "@/lib/glossary";
import { useT } from "@/lib/i18n";
import { HelpHint } from "@/components/ui/HelpHint";
import { carryTo, useNavigateCarrying } from "@/lib/carryParams";
import { exportSeriesCsv } from "@/lib/exportCsv";
import { signedKW, formatV, formatRelativeAge, bessDirection } from "@/lib/format";
import { autoGran, type Granularity } from "@/lib/timeRange";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Download, Filter, Search, Star, StarOff, WifiOff, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { chartTokens } from "@/lib/chartTheme";

const DEFAULT_KEYS = ["ac_active_power", "soc"];

// Granularities this page's TimeRangeBar exposes — kept in one place so
// the zoom-driven autoGran (HIST-05) picks from the same allowlist.
// Matches the TimeRangeBar default set.
const BESS_GRANS: Granularity[] = ["raw", "15m", "h", "d", "w", "m", "y"];

const GROUP_TONES: Record<string, string> = {
  power: "charge",
  energy: "export",
  soc: "accent",
  voltage: "import",
  current: "import",
  temperature: "warn",
  fault: "critical",
  command: "discharge",
  diagnostic: "muted",
};

export default function BessDetailPage() {
  const t = useT();
  const glossary = useGlossary();
  const { id } = useParams<{ id: string }>();
  const adapter = useAdapter();
  const snap = useSnapshot();
  const recent = useRecent();
  const topology = useTopology();
  const navigate = useNavigate();
  const navigateCarrying = useNavigateCarrying();
  const [searchParams] = useSearchParams();

  const config = topology.bess.find((b) => b.id === id);
  const unit = snap.bess_per_unit.find((u) => u.id === id);
  const catalog = useMemo(() => (config ? bessTelemetryCatalog(config) : []), [config]);
  // Human name for a catalog key (BESS-10 / CLAR-14) — used for the chart
  // series names, the card title and toasts; raw keys stay in the URL/wire.
  const labelFor = (k: string) => catalog.find((c) => c.key === k)?.label ?? k;
  const [filter, setFilter] = useState("");
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  // Last inspection per unit (BESS-04 / PERS-06): when the URL carries no
  // ?keys the unit re-opens on its previous selection (written below on
  // every change) before falling back to the hardcoded default.
  const [prefKeys, setPrefKeys] = useUiPref<string[]>(`bessDetail.keys:${id}`, DEFAULT_KEYS);
  // Site-wide pinned keys (BESS-04): float to the top of the browser
  // list. Re-validated against the catalog on every read so keys that
  // left the catalog are dropped silently.
  const [storedPinned, setStoredPinned] = useUiPref<string[]>("bessDetail.pinned", []);
  const pinned = useMemo(
    () => (Array.isArray(storedPinned) ? storedPinned.filter((k) => catalog.some((c) => c.key === k)) : []),
    [storedPinned, catalog],
  );
  // ?keys= mirrors the telemetry-browser selection (PERS-06 / HIST-09) so
  // unit inspections are shareable and survive reloads. Hydration
  // precedence: URL → pref → default, every layer validated against the
  // SYNCHRONOUS per-unit catalog — unlike Historical there is no
  // async-validation race. syncWhen gates URL writes off the not-found page.
  const [selected, setSelected] = useUrlParamState<string[]>(
    "keys",
    (raw) => {
      if (raw) {
        const valid = raw.split(",").map((s) => s.trim()).filter((k) => catalog.some((c) => c.key === k));
        if (valid.length) return valid.slice(0, 4);
      }
      const fromPref = Array.isArray(prefKeys)
        ? prefKeys.filter((k) => catalog.some((c) => c.key === k))
        : [];
      return fromPref.length ? fromPref.slice(0, 4) : DEFAULT_KEYS;
    },
    (v) => (v.length ? v.join(",") : null),
    { syncWhen: catalog.length > 0 },
  );

  // Remember the selection as this unit's last inspection (PERS-06).
  // Gated off the not-found page (empty catalog) like the URL sync.
  useEffect(() => {
    if (catalog.length === 0) return;
    setPrefKeys(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.join(","), setPrefKeys]);

  const [range, setRange] = useUrlTimeRange("24h");
  const sum = bessChartSummary(recent, id);

  const visible = useMemo(() => {
    const matches = catalog.filter((k) =>
      (filter === "" || k.key.toLowerCase().includes(filter.toLowerCase()) || k.label.toLowerCase().includes(filter.toLowerCase()) || k.description.toLowerCase().includes(filter.toLowerCase()))
      && (activeGroup == null || k.group === activeGroup),
    );
    // Pinned keys sort to the top; Array.prototype.sort is stable, so
    // catalog order is preserved within the pinned and unpinned halves.
    return matches.sort((a, b) => Number(pinned.includes(b.key)) - Number(pinned.includes(a.key)));
  }, [catalog, filter, activeGroup, pinned]);

  const {
    data: history,
    isFetching: historyFetching,
    isError: historyIsError,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ["bess-history", id, selected, range.start, range.end, range.gran],
    queryFn: () => adapter.history({ bess_id: id, keys: selected, start_ts: range.start, end_ts: range.end, granularity: range.gran }),
    enabled: selected.length > 0,
  });

  // Zoom = range = URL (HIST-05): a datazoom gesture on the historical
  // chart below adopts the zoomed window as the shared range — same
  // guards as HistoricalPage: ignore echoes that equal the current range
  // within 1 s (the chart re-renders full-width after each refetch and
  // fires a datazoom reset), and ignore events while this window's fetch
  // is in flight.
  function handleZoomWindow(startTs: number, endTs: number) {
    if (historyFetching) return;
    if (Math.abs(startTs - range.start) < 1000 && Math.abs(endTs - range.end) < 1000) return;
    setRange({ start: startTs, end: endTs, gran: autoGran(endTs - startTs, BESS_GRANS), preset: "custom" });
  }

  // Repaint on theme flips (DSGN-07): colours are resolved INSIDE the
  // memo via chartTokens() with themeVersion in the deps — the previous
  // module-level palette snapshot froze series colours at first load.
  const themeVersion = useThemeVersion();
  const series = useMemo<HistorySeries[]>(() => {
    if (!history) return [];
    const t = chartTokens();
    // Direction/domain hues only — severity colours (warn/critical) are
    // alarm vocabulary and never paint healthy series (DSGN-08).
    const cycleColors = [t.charge, t.solar, t.import, t.export];
    return selected.map((k, idx) => {
      const meta = catalog.find((c) => c.key === k);
      return {
        // Human series name (BESS-10) — the raw key stays visible in the
        // browser list's mono secondary line.
        name: meta?.label ?? k,
        unit: meta?.unit ?? "",
        data: history.series[k] ?? [],
        // SOC wears the brand accent everywhere — the same choice as
        // Historical's curated "Avg SOC" entry (DSGN-08).
        color: k === "soc" ? t.accent : cycleColors[idx % cycleColors.length],
        yAxis: meta?.unit === "%" ? 1 : 0,
        type: meta?.chart === "step" ? "step" : "line",
        fill: idx === 0,
      };
    });
  }, [history, selected, catalog, themeVersion]);

  if (!config || !unit) {
    return (
      <PageShell title={t("analyze.bessDetail.notFoundTitle")} caption={t("analyze.bessDetail.notFoundCaption", { id: id ?? "", topo: topology.id })}>
        <button onClick={() => navigate("/bess")} className="rounded-md border border-edge bg-surface px-3 py-1.5 text-xs">
          {t("analyze.bessDetail.backToFleet")}
        </button>
      </PageShell>
    );
  }

  // Unified idle noise floor (see format.ts:IDLE_DEADBAND_W) — same
  // charge/discharge/idle resolution as BessTile, so this page can't
  // claim "Charging" on sensor noise the fleet tile calls idle.
  const powerDir = bessDirection(unit.active_W);

  // Unit switcher (BESS-05): hop units while the forensic window AND the
  // ?keys selection survive — carried explicitly since `keys` is page-local
  // and not in the carry allowlist.
  const unitIdx = topology.bess.findIndex((b) => b.id === unit.id);
  const prevUnit = unitIdx > 0 ? topology.bess[unitIdx - 1] : null;
  const nextUnit = unitIdx >= 0 && unitIdx < topology.bess.length - 1 ? topology.bess[unitIdx + 1] : null;
  const goUnit = (targetId: string) =>
    navigateCarrying(`/bess/${targetId}`, { keys: selected.length ? selected.join(",") : null });

  // Labelled origin breadcrumb (N22): an alarm's "Open device" drill arrives
  // with ?from=alarm:<id>. Surface ONE "Back to alarm" link that re-opens the
  // triaged row via AlarmsPage's ?id= re-expand contract, stripping `from` on
  // the way back so it can't outlive its meaning. The token is intentionally
  // dropped on a unit switch (goUnit doesn't carry it), so the return promise
  // never points at an alarm that didn't raise on the unit you're now viewing.
  const fromAlarmId = (() => {
    const raw = searchParams.get("from");
    return raw && raw.startsWith("alarm:") ? raw.slice("alarm:".length) : null;
  })();
  const backToAlarmHref = fromAlarmId
    ? carryTo("/alarms", { id: fromAlarmId, from: null })
    : null;

  // Lossless Historical handoff (BESS-01 / HIST-01 / LINK-02 / CONS-04):
  // translate catalog keys into the device:<entityId>:<rawKey> namespace
  // Historical accepts and pin the current window. When nothing maps,
  // omit ?keys so Historical falls back to its curated defaults.
  const histKeys = adapter.historicalKeysFor(unit.id, selected);
  const histHref = carryTo("/historical", {
    bess: unit.id,
    start: String(range.start),
    end: String(range.end),
    gran: range.gran,
    preset: range.preset,
    ...(histKeys.length ? { keys: histKeys.join(",") } : {}),
  });

  return (
    <PageShell
      title={`${unit.name} · ${config.family}`}
      showScope={false}
      caption={t("analyze.bessDetail.caption", { proto: config.protocol, host: `${config.host}:${config.port}`, cap: config.capacity_kWh })}
      actions={
        /* carryTo / goUnit: fleet return and unit hops keep the forensic window (BESS-05). */
        <>
          <div className="flex h-8 items-center gap-0.5 rounded-md border border-edge bg-surface px-1">
            <button
              onClick={() => prevUnit && goUnit(prevUnit.id)}
              disabled={!prevUnit}
              title={prevUnit ? t("analyze.bessDetail.prevUnit", { name: prevUnit.name }) : t("analyze.bessDetail.firstUnit")}
              aria-label={t("analyze.bessDetail.prevAria")}
              className="grid h-6 w-6 place-items-center rounded text-text-muted hover:text-text disabled:cursor-default disabled:opacity-30"
            >
              <ChevronLeft size={13} />
            </button>
            <select
              value={unit.id}
              onChange={(e) => goUnit(e.target.value)}
              aria-label={t("analyze.bessDetail.switchAria")}
              className="h-6 bg-transparent text-xs text-text outline-none"
            >
              {topology.bess.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <button
              onClick={() => nextUnit && goUnit(nextUnit.id)}
              disabled={!nextUnit}
              title={nextUnit ? t("analyze.bessDetail.nextUnit", { name: nextUnit.name }) : t("analyze.bessDetail.lastUnit")}
              aria-label={t("analyze.bessDetail.nextAria")}
              className="grid h-6 w-6 place-items-center rounded text-text-muted hover:text-text disabled:cursor-default disabled:opacity-30"
            >
              <ChevronRight size={13} />
            </button>
          </div>
          {/* N22 — labelled origin breadcrumb: only when we arrived from an
              alarm (?from=alarm:<id>). Re-opens that alarm via AlarmsPage's
              ?id= re-expand. Same h-8 border-edge button grammar as Fleet so
              it inherits print/theme/narrow-iframe behaviour for free. */}
          {backToAlarmHref && (
            <Link to={backToAlarmHref} className="flex h-8 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent/40">
              <ArrowLeft size={12} /> {t("analyze.bessDetail.backToAlarm")}
            </Link>
          )}
          <Link to={carryTo("/bess")} className="flex h-8 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent/40">
            <ArrowLeft size={12} /> {t("analyze.bessDetail.fleet")}
          </Link>
        </>
      }
    >
      {/* Offline truth banner (BESS-03): without it this page presented the
          last-received values as live. Heartbeat-age guard mirrors BessTile:
          0 = no heartbeat received yet, 9999 = mock-adapter sentinel —
          neither is a real age, so don't render one. */}
      {!unit.online && (
        <div className="flex items-center gap-3 rounded-lg border border-critical/40 bg-critical/10 px-4 py-3 text-sm text-critical">
          <WifiOff size={16} className="shrink-0" />
          <span>
            <strong>{t("status.offline")}</strong>
            {" — "}
            {unit.heartbeat_age_s > 0 && unit.heartbeat_age_s !== 9999
              ? t("analyze.bessDetail.lastHeartbeat", { age: formatRelativeAge(unit.heartbeat_age_s * 1000) })
              : t("analyze.bessDetail.noRecentData")}
            {" · "}
            {t("analyze.bessDetail.offlineTail")}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <SocArc pct={unit.soc_pct} size={84} strokeWidth={9} />
            <div>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-text-faint">
                {t("analyze.bessDetail.soc")}
                {/* normal-case/tracking-normal: don't let the title's
                    uppercase/wider inherit into the popover prose. */}
                <HelpHint className="normal-case tracking-normal" title={glossary.soc.term}>{glossary.soc.def}</HelpHint>
              </div>
              <div className={cn("text-3xl font-bold num", unit.online ? "text-charge" : "text-stale")}>{unit.soc_pct.toFixed(1)}<span className="text-sm text-text-muted">%</span></div>
              <div className="text-[11px] text-text-muted">
                {t("analyze.bessDetail.range60")} <span className="num text-text">{sum.soc_min.toFixed(0)}–{sum.soc_max.toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-text-faint">{t("analyze.bessDetail.activePower")}</div>
          <div className="mt-1 flex items-baseline gap-3">
            <div className={cn(
              "text-3xl font-bold num",
              // Stale tone wins when offline — the value is the last
              // received, not a live reading (BESS-03).
              !unit.online && "text-stale",
              unit.online && powerDir === "charge" && "text-charge",
              unit.online && powerDir === "discharge" && "text-discharge",
            )}>
              {signedKW(unit.active_W)}
              <span className="ml-1 text-sm text-text-muted">kW</span>
            </div>
            <div className="text-[11px] text-text-muted">
              {powerDir === "charge" ? t("status.charging") : powerDir === "discharge" ? t("status.discharging") : t("status.idle")}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-edge pt-3 text-[11px]">
            <Stat label={t("analyze.bessDetail.dcBus")} value={formatV(unit.total_voltage_V)} help="dcBus" />
            <Stat label={t("analyze.bessDetail.reactive")} value={`${(unit.reactive_VAr / 1000).toFixed(1)} kVAr`} help="reactive" />
            <Stat label={t("analyze.bessDetail.temp")} value={`${unit.ambient_temperature_C.toFixed(1)} °C`} />
            <Stat label="BESS ON/OFF" value={unit.bess_onoff ? "ON" : "OFF"} tone={unit.bess_onoff ? "ok" : "stale"} />
          </div>
        </Card>
        <Card className={cn("p-5", unit.fault_count > 0 && "border-warn/30")}>
          <div className="text-[10px] uppercase tracking-wider text-text-faint">{t("analyze.bessDetail.faultFlags")}</div>
          <div className={cn("mt-1 text-3xl font-bold num", !unit.online && "text-stale")}>{unit.fault_count}</div>
          {unit.fault_summary ? (
            <>
              {/* The big number counts flag BITS; the chip is a decoded
                  summary, not an exhaustive list — say so when they differ
                  so the gap doesn't read as hidden faults. */}
              {unit.fault_count > 1 && (
                <div className="mt-1 text-[10px] text-text-muted">{t("analyze.bessDetail.flagBitsSummary", { n: unit.fault_count })}</div>
              )}
              <div className="mt-2 rounded-md border border-warn/30 bg-warn/10 px-2 py-1.5 text-[11px] text-warn">{unit.fault_summary}</div>
            </>
          ) : unit.fault_count > 0 ? (
            <div className="mt-2 text-[11px] text-text-muted">{t("analyze.bessDetail.flagBitsNoSummary", { n: unit.fault_count })}</div>
          ) : (
            <div className="mt-2 text-[11px] text-text-muted">{t("analyze.bessDetail.noFaults")}</div>
          )}
          <div className="mt-3 border-t border-edge pt-3 text-[11px] text-text-muted">
            {t("analyze.bessDetail.watchdog")} <span className="num text-text">{config.operating_profile?.watchdog_action ?? "zero_then_off"}</span> · {t("analyze.bessDetail.cmdTimeout", { s: config.operating_profile?.setpoint_timeout_s ?? 20 })}
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader>
          {/* Offline unit: the 60-min buffer is no longer being fed, so the
              badge drops the green "live" claim and turns stale (BESS-03). */}
          <CardTitle hint={<GranBadge live={unit.online} stale={!unit.online} label={t("analyze.bess.last60")} />}>{t("analyze.bessDetail.powerSocTitle", { name: unit.name })}</CardTitle>
          <div className="flex flex-wrap items-center gap-3 text-[10px] text-text-muted">
            <span>{t("analyze.bessDetail.socNow")} <strong className="num text-text">{sum.soc_now.toFixed(1)}%</strong></span>
            <span>{t("analyze.bessDetail.range")} <strong className="num text-text">{sum.soc_min.toFixed(0)} → {sum.soc_max.toFixed(0)}%</strong></span>
            <span>{t("analyze.bessDetail.pRange")} <strong className="num text-text">{sum.p_min_kW.toFixed(1)} → {sum.p_max_kW.toFixed(1)} kW</strong></span>
            <span>{t("analyze.bess.statAvg")} <strong className="num text-text">{sum.p_avg_kW.toFixed(1)} kW</strong></span>
          </div>
        </CardHeader>
        <div className="p-3">
          <BessSocPowerChart snapshots={recent} bessId={unit.id} height={240} />
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle hint={<GranBadge live={unit.online} stale={!unit.online} label={t("analyze.bess.last60")} />}>{t("analyze.bessDetail.cmdVsMeas")}</CardTitle>
          <span className="text-[10px] text-text-muted">
            {t("analyze.bessDetail.cmdVsMeasSub")}
          </span>
        </CardHeader>
        <div className="p-3">
          <SetpointTracker snapshots={recent} bessId={unit.id} height={220} />
        </div>
      </Card>

      <TimeRangeBar range={range} onChange={setRange} granularities={BESS_GRANS} />

      {/* Availability over the selected window — gap segments deep-link to
          the forensic Historical view (round-3 outage visibility). */}
      <Card className="p-4">
        <AvailabilityRibbon target={{ kind: "bess", id: unit.id }} start={range.start} end={range.end} />
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>{t("analyze.bessDetail.telemetryBrowser")}</CardTitle>
            <span className="text-[10px] text-text-muted">{t("analyze.bessDetail.keysCount", { n: catalog.length })}</span>
          </CardHeader>
          <div className="flex items-center gap-2 border-b border-edge px-3 py-2">
            <Search size={12} className="text-text-muted" />
            <input
              placeholder={t("analyze.bessDetail.filterPlaceholder")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="flex-1 bg-transparent text-xs outline-none placeholder:text-text-faint"
            />
            {filter && (
              <button className="text-text-muted hover:text-text" onClick={() => setFilter("")}>
                <X size={12} />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1 border-b border-edge px-3 py-2 text-[10px]">
            <button
              onClick={() => setActiveGroup(null)}
              className={cn("rounded-md border border-edge bg-surface-2 px-2 py-0.5", activeGroup == null && "border-accent/40 bg-accent/15 text-accent")}
            >
              {t("analyze.bessDetail.all")}
            </button>
            {TELEMETRY_GROUPS.map((g) => (
              <button
                key={g}
                onClick={() => setActiveGroup(activeGroup === g ? null : g)}
                className={cn(
                  "rounded-md border px-2 py-0.5",
                  activeGroup === g
                    ? `border-${GROUP_TONES[g]}/40 bg-${GROUP_TONES[g]}/15 text-${GROUP_TONES[g]}`
                    : "border-edge bg-surface-2 text-text-muted",
                )}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {visible.map((k) => {
              const isSelected = selected.includes(k.key);
              const isPinned = pinned.includes(k.key);
              return (
                <div
                  key={k.key}
                  className={cn(
                    "flex w-full items-start gap-2 border-b border-edge/60 px-3 py-2 hover:bg-surface-2",
                    isSelected && "bg-accent/10",
                  )}
                >
                  <button
                    onClick={() => {
                      if (isSelected) {
                        setSelected(selected.filter((x) => x !== k.key));
                        return;
                      }
                      const next = [...selected, k.key].slice(-4);
                      // 4-key cap evicts the oldest key — say so instead of
                      // letting a watched series vanish silently (BESS-12).
                      const evicted = selected.find((x) => !next.includes(x));
                      if (evicted) pushToast({ level: "info", title: t("analyze.bessDetail.replacedToast", { label: labelFor(evicted) }) });
                      setSelected(next);
                    }}
                    className="flex flex-1 items-start gap-2 text-left"
                  >
                    <input type="checkbox" checked={isSelected} readOnly className="mt-0.5 h-3 w-3 accent-accent" />
                    <div className="flex-1">
                      {/* Human label first (BESS-10); the raw wire key +
                          topic stay below as the mono secondary line. */}
                      <div className="text-[12px] font-semibold">{k.label} <span className="ml-1 text-[10px] text-text-faint">{k.unit}</span></div>
                      <div className="text-[10px] text-text-muted">{k.description}</div>
                      <div className="mt-1 text-[9px] font-mono text-text-faint">{k.key} · {k.topic}</div>
                    </div>
                  </button>
                  {/* Pin/star (BESS-04): pinned keys sort to the top of the
                      list on every visit, site-wide across units. */}
                  <button
                    onClick={() => setStoredPinned(isPinned ? pinned.filter((x) => x !== k.key) : [...pinned, k.key])}
                    title={isPinned ? t("analyze.bessDetail.unpinTitle") : t("analyze.bessDetail.pinTitle")}
                    aria-label={isPinned ? t("analyze.bessDetail.unpinAria", { label: k.label }) : t("analyze.bessDetail.pinAria", { label: k.label })}
                    aria-pressed={isPinned}
                    className="mt-0.5 shrink-0 rounded p-0.5 text-text-faint transition hover:text-accent"
                  >
                    {isPinned ? <Star size={12} className="fill-accent text-accent" /> : <StarOff size={12} />}
                  </button>
                </div>
              );
            })}
            {visible.length === 0 && (
              <div className="px-3 py-6 text-center text-[11px] text-text-muted">{t("analyze.bessDetail.noKeysMatch")}</div>
            )}
          </div>
          <div className="border-t border-edge bg-surface-2 px-3 py-2 text-[10px] text-text-muted">
            {t("analyze.bessDetail.footerToggle")} <strong className="text-text">{selected.length}</strong>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle hint={<GranBadge label={t(`analyze.granLabel.${range.gran}`)} />}>
              {t("analyze.historical.title")} · {selected.length ? selected.map(labelFor).join(" / ") : t("analyze.bessDetail.selectKey")}
            </CardTitle>
            <div className="flex items-center gap-3">
              {/* Export CSV (HIST-06 / CONS-09): serialise exactly the
                  selected telemetry over the current window — same
                  affordance grammar as Overview's export. */}
              <button
                onClick={() => exportSeriesCsv(
                  `${unit.id}-telemetry-${fileStamp(range.start)}-${fileStamp(range.end)}.csv`,
                  series,
                )}
                disabled={series.every((s) => s.data.length === 0)}
                title={t("analyze.historical.exportTitle")}
                className="flex h-7 items-center gap-1.5 rounded-md border border-edge bg-surface px-2 text-[11px] hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download size={12} /> {t("common.exportCsv")}
              </button>
              <Link to={histHref} className="flex items-center gap-1 text-xs text-accent hover:underline">
                {t("analyze.bessDetail.openInHistorical")} <ArrowRight size={11} />
              </Link>
            </div>
          </CardHeader>
          <div className="p-3">
            {selected.length === 0 ? (
              <div className="grid h-[320px] place-items-center text-xs text-text-muted">
                <div className="text-center">
                  <Filter size={20} className="mx-auto mb-2 text-text-faint" />
                  {t("analyze.bessDetail.selectUpTo4")}
                </div>
              </div>
            ) : historyIsError && history === undefined ? (
              /* Error before loading (CONS-02): a failed fetch leaves
                 `history` undefined forever — without this branch the
                 page shows an eternal skeleton indistinguishable from a
                 slow query. Errors on a background refetch keep the last
                 good data rendered instead. Mirrors HistoricalPage. */
              <EmptyState
                title={t("analyze.err.loadHistoryTitle")}
                detail={historyError instanceof Error && historyError.message
                  ? historyError.message
                  : t("analyze.err.requestFailed")}
                action={
                  <button
                    onClick={() => refetchHistory()}
                    className="rounded-md border border-edge bg-surface px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-accent hover:text-accent"
                  >
                    {t("common.retry")}
                  </button>
                }
                height={320}
              />
            ) : history === undefined ? (
              /* Loading ≠ empty (DSGN-13): shimmer while fetching; the
                 dashed EmptyState is reserved for resolved-empty/error. */
              <ChartSkeleton height={320} />
            ) : series.every((s) => s.data.length === 0) ? (
              <EmptyState
                title={t("analyze.bessDetail.emptyTitle")}
                detail={t("analyze.bessDetail.emptyDetail", { keys: selected.map(labelFor).join(", ") })}
                height={320}
              />
            ) : (
              <HistoryChart series={series} height={320} onWindowChange={handleZoomWindow} />
            )}
          </div>
        </Card>
      </div>
    </PageShell>
  );
}

function Stat({ label, value, tone, help }: { label: string; value: string; tone?: "ok" | "stale" | "warn"; help?: GlossaryKey }) {
  const glossary = useGlossary();
  const c = tone === "ok" ? "text-ok" : tone === "stale" ? "text-stale" : tone === "warn" ? "text-warn" : "text-text";
  return (
    <div>
      <div className="flex items-center gap-1 text-text-faint">
        {label}
        {/* Glossary hint (CLAR-08): same glossary+HelpHint pattern as the
            RightNowStrip cells — wording lives in lib/glossary only. */}
        {help && <HelpHint title={glossary[help].term}>{glossary[help].def}</HelpHint>}
      </div>
      <div className={cn("font-semibold num", c)}>{value}</div>
    </div>
  );
}

/** ISO-8601 stamp for export filenames — colons stripped (basic time
 *  format) so the name survives every filesystem the browser saves to. */
function fileStamp(ts: number): string {
  return new Date(ts).toISOString().slice(0, 19).replace(/:/g, "") + "Z";
}
